/**
 * useWorldCupGroupDetailData — Adapter hook for the group detail page.
 *
 * Fetches a squad's leaderboard from:
 *   GET /competitions/:competitionId/groups/:groupId/leaderboard
 *
 * No client-side mock leaderboard or group metadata — API only.
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import {
  WORLD_CUP_GROUP_NAME_FALLBACK,
} from '../worldcup.constants';
import { useCompetitionId } from '../../competitions/contexts/CompetitionContext';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import { mapLeaderboardApiEntry } from '../helpers/worldcup.helper';
import { resolveLeaderboardSelfRow } from '../helpers/leaderboardSelfRow.helper';
import {
  useWorldCupGroupLeaderboardInfiniteQuery,
  type GroupLeaderboardPeriod,
} from './useWorldCupGroupLeaderboardQuery';
import {
  SQUAD_LEADERBOARD_PAGE_SIZE,
  pageFromRank,
} from '../helpers/squadLeaderboardPagination.helper';

/** Default placeholder emoji for a World Cup squad when no branded icon is set. */
const WORLD_CUP_GROUP_DEFAULT_EMOJI = '⚽';
import type { GroupsApiEntry } from './useWorldCupGroupsQuery';
import type {
  WorldCupGroup,
  LeaderboardEntry,
  WorldCupMembershipRole,
} from '../types/worldcup.types';

interface WorldCupGroupDetailData {
  group: WorldCupGroup | null;
  leaderboard: LeaderboardEntry[];
  top3: LeaderboardEntry[];
  rest: LeaderboardEntry[];
  /** Current user row pinned below the list (Leaderboard-style); undefined if not in data or no "me" row. */
  pinnedCurrentUser: LeaderboardEntry | undefined;
  /**
   * The caller's leaderboard row regardless of where it sits — the on-page entry
   * when they're in the fetched slice, otherwise the server-provided off-page row.
   * Null when the caller has no row (spectator / not a member). Powers the always-
   * visible "your standing" bar so low-ranked members never have to scroll to find
   * themselves.
   */
  currentUserEntry: LeaderboardEntry | null;
  /** True when the caller's row is present in the rendered list (top3 + rest). */
  isCurrentUserOnPage: boolean;
  /** The caller's role in this squad, or null when not a member. */
  currentUserRole: WorldCupMembershipRole | null;
  /** The caller's CompetitionMembership id in this squad, or null when not a member. */
  currentUserMembershipId: string | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  /**
   * True only when the leaderboard query is serving placeholder (previous period's) data
   * while a new period fetch is in flight. Page renders chrome normally and swaps just
   * the podium+rows section to a skeleton based on this flag.
   */
  isLeaderboardSectionLoading: boolean;
  /** True when more pages remain to load (Story 9 — "Show more" / scroll-end). */
  hasNextPage: boolean;
  /** True while the next page is being fetched. */
  isFetchingNextPage: boolean;
  /** Loads the next page of squad members (no-op when none remain or already fetching). */
  fetchNextPage: () => void;
  /**
   * Loads pages up to and including the page that contains the current user's row, so
   * jump-to-me can scroll to it. Resolves once the row is loaded (or no more pages).
   */
  loadUpToSelfPage: () => Promise<void>;
}


export function useWorldCupGroupDetailData(
  groupId: string,
  period: GroupLeaderboardPeriod = 'overall',
): WorldCupGroupDetailData {
  const competitionId = useCompetitionId();
  const queryClient = useQueryClient();
  const { isSignedIn, isLoaded } = useSupabaseAuth();
  const canFetch = isLoaded && isSignedIn && groupId.length > 0;
  const groupLeaderboardQuery = useWorldCupGroupLeaderboardInfiniteQuery(
    competitionId,
    groupId,
    { enabled: canFetch, limit: SQUAD_LEADERBOARD_PAGE_SIZE, period },
  );

  /** First page carries group metadata; last page carries the freshest currentUser + total. */
  const pages = groupLeaderboardQuery.data?.pages;
  const firstPage = pages?.[0];
  const lastPage = pages?.[pages.length - 1];

  /** Flattened, in-order entries across all loaded pages (contiguous from page 1). */
  const rawEntries = useMemo(
    () => (pages ? pages.flatMap((p) => p.entries) : []),
    [pages],
  );

  const leaderboard = useMemo(() => {
    if (!isLoaded || !isSignedIn) return [];
    const entries = rawEntries;
    /**
     * Group route doesn't set `rank` on entries — derive from sorted-array index
     * (the server orders by points before returning). Without this every row lands
     * with rank 0 and the podium can't slot ranks 1/2/3.
     */
    return entries.map((entry, index) => ({
      ...mapLeaderboardApiEntry(entry),
      rank: entry.rank ?? index + 1,
    }));
  }, [rawEntries, isLoaded, isSignedIn]);

  const group = useMemo<WorldCupGroup | null>(() => {
    if (!isLoaded || !isSignedIn) return null;
    const apiData = firstPage;
    if (!apiData) return null;

    const entries = leaderboard;
    const apiViewerRole = apiData.group?.viewerRole;
    const viewerRoleFromApi =
      apiViewerRole === 'ADMIN' || apiViewerRole === 'MEMBER' ? apiViewerRole : null;

    const cachedRows = queryClient.getQueryData<GroupsApiEntry[]>(
      worldCupQueryKeys.groups(competitionId),
    );
    const cachedRow = cachedRows?.find((g) => g.id === groupId);
    const cachedViewerRole = cachedRow?.userRole;
    const viewerRoleFromCache =
      cachedViewerRole === 'ADMIN' || cachedViewerRole === 'MEMBER' ? cachedViewerRole : null;

    const viewerRole = viewerRoleFromApi ?? viewerRoleFromCache;
    const viewerCanLeaveFromApi = apiData.group?.viewerCanLeaveSquad;
    const viewerCanLeaveSquad =
      typeof viewerCanLeaveFromApi === 'boolean'
        ? viewerCanLeaveFromApi
        : viewerRole !== 'ADMIN';
    const cachedName = cachedRow?.name?.trim();
    const apiName = apiData.group?.name?.trim();
    const resolvedName =
      (apiName && apiName.length > 0 ? apiName : undefined) ??
      (cachedName && cachedName.length > 0 ? cachedName : undefined) ??
      WORLD_CUP_GROUP_NAME_FALLBACK;
    const resolvedCompanyBranding = apiData.group?.companyBranding ?? cachedRow?.companyBranding ?? null;
    const resolvedCrestColor = apiData.group?.crestColor ?? cachedRow?.crestColor ?? null;
    const resolvedCrestEmblem = apiData.group?.crestEmblem ?? cachedRow?.crestEmblem ?? null;
    const inviteFromApi =
      apiData.group?.inviteCode != null ? String(apiData.group.inviteCode).trim() : '';
    const inviteFromCache =
      cachedRow?.inviteCode != null ? String(cachedRow.inviteCode).trim() : '';
    const resolvedInviteCode =
      inviteFromApi.length > 0 ? inviteFromApi : inviteFromCache.length > 0 ? inviteFromCache : null;

    const currentUserEntry = entries.find((entry) => entry.isCurrentUser);
    const topEntry = entries[0];
    return {
      id: groupId,
      name: resolvedName,
      viewerRole,
      viewerCanLeaveSquad,
      inviteCode: resolvedInviteCode,
      emoji: WORLD_CUP_GROUP_DEFAULT_EMOJI,
      memberCount: apiData.pagination.total,
      userRank: currentUserEntry?.rank ?? 0,
      userPoints: currentUserEntry?.points ?? 0,
      userHits: currentUserEntry?.exactHitCount ?? 0,
      leadingUserName: topEntry?.name,
      companyBranding: resolvedCompanyBranding,
      crestColor: resolvedCrestColor,
      crestEmblem: resolvedCrestEmblem,
      memberPreview: cachedRow?.memberPreview,
      description: cachedRow?.description,
      entryFeeCoins: cachedRow?.entryFeeCoins,
      payoutMode: cachedRow?.payoutMode,
      payoutFirstPct: cachedRow?.payoutFirstPct,
      payoutSecondPct: cachedRow?.payoutSecondPct,
      payoutThirdPct: cachedRow?.payoutThirdPct,
    };
  }, [groupId, firstPage, isLoaded, isSignedIn, leaderboard, queryClient]);

  /** Freshest current-user row: last loaded page, falling back to the first. */
  const apiCurrentUser = lastPage?.currentUser ?? firstPage?.currentUser ?? null;

  const { top3, rest, pinnedCurrentUser, currentUserEntry, isCurrentUserOnPage } = useMemo(() => {
    /**
     * Slice by sorted-array position, not by `rank`. The overall route assigns
     * shared ranks to ties (sortLeaderboard), so pre-kickoff every member is
     * rank 1 — a rank-based filter dumps them all into top3 and leaves rest
     * empty, and the podium then drops all but one of them.
     */
    const top3Slice = leaderboard.slice(0, 3);
    const restSlice = leaderboard.slice(3);
    const onPageEntry = leaderboard.find((entry) => entry.isCurrentUser) ?? null;
    // Off the page slice the list doesn't carry the row, so reconstruct it from
    // the server-provided current-user payload (correct global rank).
    const offPageEntry = apiCurrentUser ? mapLeaderboardApiEntry(apiCurrentUser) : null;
    const self = resolveLeaderboardSelfRow({ rawEntries, onPageEntry, offPageEntry });

    /**
     * Keep "me" in the main list when on-page (excluding them made global rank #1
     * look visually like #2); pin the off-page row below the list otherwise.
     */
    const pinnedCurrentUser = !self.isCurrentUserOnPage && offPageEntry ? offPageEntry : undefined;

    return { top3: top3Slice, rest: restSlice, pinnedCurrentUser, ...self };
  }, [leaderboard, rawEntries, apiCurrentUser]);

  const isLoading = Boolean(!isLoaded || (isSignedIn && groupLeaderboardQuery.isLoading));
  const isFetching = Boolean(isSignedIn && groupLeaderboardQuery.isFetching);
  const isError = Boolean(isSignedIn && groupLeaderboardQuery.isError);
  const isLeaderboardSectionLoading = Boolean(groupLeaderboardQuery.isPlaceholderData);

  const currentUserRole: WorldCupMembershipRole | null = group?.viewerRole ?? null;

  const currentUserMembershipId: string | null = useMemo(() => {
    if (apiCurrentUser?.id) return apiCurrentUser.id;
    const onPage = rawEntries.find((e) => e.isCurrentUser === true);
    return onPage?.id ?? null;
  }, [apiCurrentUser, rawEntries]);

  /**
   * Jump-to-me support: load pages until the page containing the viewer's row is
   * loaded, then the component scrolls to it. The viewer's global rank comes from
   * the server-provided `currentUser` row; `pageFromRank` gives the target page.
   * Sequential `fetchNextPage` is bounded by a safety cap so a very large squad
   * can't trigger an unbounded fetch loop (true direct-page jump is a SCALE-1
   * follow-up — see spec AC-47).
   */
  const loadUpToSelfPage = useCallback(async () => {
    const rank = apiCurrentUser?.rank ?? 0;
    if (rank < 1) return;
    const targetPage = pageFromRank(rank, SQUAD_LEADERBOARD_PAGE_SIZE);
    let guard = targetPage + 2;
    while (
      guard-- > 0 &&
      (groupLeaderboardQuery.data?.pages.length ?? 0) < targetPage &&
      groupLeaderboardQuery.hasNextPage &&
      !groupLeaderboardQuery.isFetchingNextPage
    ) {
      // eslint-disable-next-line no-await-in-loop
      await groupLeaderboardQuery.fetchNextPage();
    }
  }, [apiCurrentUser?.rank, groupLeaderboardQuery]);

  const fetchNextPage = useCallback(() => {
    if (groupLeaderboardQuery.hasNextPage && !groupLeaderboardQuery.isFetchingNextPage) {
      void groupLeaderboardQuery.fetchNextPage();
    }
  }, [groupLeaderboardQuery]);

  return {
    group,
    leaderboard,
    top3,
    rest,
    pinnedCurrentUser,
    currentUserEntry,
    isCurrentUserOnPage,
    currentUserRole,
    currentUserMembershipId,
    isLoading,
    isFetching,
    isError,
    isLeaderboardSectionLoading,
    hasNextPage: Boolean(groupLeaderboardQuery.hasNextPage),
    isFetchingNextPage: Boolean(groupLeaderboardQuery.isFetchingNextPage),
    fetchNextPage,
    loadUpToSelfPage,
  };
}
